/**
 * Settings → Messaging's Email card, as data (slice 2h §8; Paper 70, 76):
 * what each address row says, the card's foot, and Change's live check.
 */
import type { EmailLink, EmailView, NameCheck } from "./api";
import { countdown } from "./view";

/** What the card shows for the crew's address. */
export type CardState = "unavailable" | "none" | "setting_up" | "ready";

export function cardState(email: EmailView | undefined): CardState {
  if (!email || email.available === false) return "unavailable";
  if (!email.address) return "none";
  return email.address.status === "current" ? "ready" : "setting_up";
}

/** An address row's state (Paper 70, 76): linked, a code out, or a code past its 10 minutes. */
export type AddressState =
  | { kind: "linked" }
  | { kind: "pending"; left: string }
  | { kind: "expired" };

export function addressState(link: EmailLink, now: number): AddressState {
  if (link.status === "linked") return { kind: "linked" };
  if (!link.expires_at || Date.parse(link.expires_at) <= now) return { kind: "expired" };
  return { kind: "pending", left: countdown(link.expires_at, now) };
}

/** The status lane's words and tone (Paper 76: "Code out · 8:41 left"). */
export function addressStatus(state: AddressState): {
  text: string;
  tone: "moss" | "copper" | "grey";
} {
  if (state.kind === "linked") return { text: "Linked", tone: "moss" };
  if (state.kind === "pending") return { text: `Code out · ${state.left} left`, tone: "copper" };
  return { text: "Code expired", tone: "grey" };
}

/** How many of the person's addresses are linked. */
function linkedCount(email: EmailView): number {
  return email.links.filter((l) => l.status === "linked").length;
}

/**
 * The card's foot (Paper 70, 76): how many of 5, and what it reads; at 5,
 * that one has to go first. On a phone only the count.
 */
export function footLine(email: EmailView, mobile: boolean): string {
  const n = linkedCount(email);
  if (n >= email.max) return `${n} of ${email.max} addresses. Remove one to add another.`;
  const count = `${n} of ${email.max} addresses`;
  if (mobile) return count;
  return `${count} · Forward it anything: a receipt, a school note, an invoice. It reads PDFs and documents too.`;
}

/** Whether Add address shows: under 5 linked (a code out doesn't count until it links). */
export function canAdd(email: EmailView): boolean {
  return linkedCount(email) < email.max;
}

/** The name as Change sends it: trimmed and lowercased, as the server keeps it. */
export function nameOf(input: string): string {
  return input.trim().toLowerCase();
}

/**
 * Change's line under the field (Paper 75, 76): free, or why not and two
 * names that are; while it checks, the rule.
 */
export function checkLine(
  name: string,
  check: NameCheck | undefined,
): { tone: "moss" | "red" | "hint"; text: string; suggestions: string[] } {
  const rule = "letters, numbers and dashes, 3 to 30";
  if (!name || !check) return { tone: "hint", text: rule, suggestions: [] };
  if (check.available) return { tone: "moss", text: `${name} is free`, suggestions: [] };
  const why: Record<NonNullable<NameCheck["reason"]>, string> = {
    invalid: `Use ${rule}.`,
    reserved: `${name} is reserved.`,
    taken: `${name} is taken.`,
    current: "That's the address now.",
  };
  return { tone: "red", text: why[check.reason ?? "invalid"], suggestions: check.suggestions };
}

/** "Jan 14": when the old address stops, 90 days from now (§3). */
export function keptUntil(now: number, timeZone: string): string {
  return new Date(now + 90 * 86_400_000).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone,
  });
}

/** The newest old address still kept after a Change (§3), and the day it stops: "Jan 14". */
export function keptLine(
  email: EmailView,
  timeZone: string,
): { full: string; until: string } | null {
  const kept = [...email.kept].sort((a, b) => b.until.localeCompare(a.until))[0];
  if (!kept) return null;
  const until = new Date(kept.until).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone,
  });
  return { full: kept.full, until };
}
